import { useCallback, useEffect, useRef } from 'react';
import { fetchPendingFollowups } from '../api';

export interface InAppNotification {
  id: string;
  type: 'followup' | 'other';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  followupId?: string;
  followupDate?: string;
  followupTime?: string;
  leadId?: string;
  leadStatus?: string;
  leadName?: string;
  leadPhone?: string;
}

const NOTIFICATION_STORE_KEY = 'crm_followup_notifications_v1';

function serializeNotifications(notifications: InAppNotification[]) {
  return notifications.map((notification) => ({
    ...notification,
    timestamp: notification.timestamp.toISOString(),
  }));
}

function deserializeNotifications(value: string | null): InAppNotification[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as Array<Omit<InAppNotification, 'timestamp'> & { timestamp: string }>;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((notification) => ({
        ...notification,
        timestamp: new Date(notification.timestamp),
      }))
      .filter((notification) => !Number.isNaN(notification.timestamp.getTime()));
  } catch {
    return [];
  }
}

function playNotificationSound() {
  if (typeof window === 'undefined') return;

  const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return;

  try {
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(660, audioContext.currentTime + 0.18);

    gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.015);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.32);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.34);

    oscillator.onended = () => {
      audioContext.close().catch(() => undefined);
    };
  } catch (error) {
    console.warn('Notification sound failed:', error);
  }
}

export function useFollowupNotifications(enabled: boolean = true) {
  const notifiedFollowups = useRef<Set<string>>(new Set());
  const inAppNotifications = useRef<InAppNotification[]>(
    typeof window === 'undefined' ? [] : deserializeNotifications(localStorage.getItem(NOTIFICATION_STORE_KEY))
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listeners = useRef<Set<(notifications: InAppNotification[]) => void>>(new Set());

  const notifyListeners = useCallback(() => {
    listeners.current.forEach((listener) => listener([...inAppNotifications.current]));
  }, []);

  const persistNotifications = useCallback((notifications: InAppNotification[]) => {
    inAppNotifications.current = notifications;
    if (typeof window !== 'undefined') {
      localStorage.setItem(NOTIFICATION_STORE_KEY, JSON.stringify(serializeNotifications(notifications)));
    }
    notifyListeners();
  }, [notifyListeners]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const checkFollowups = useCallback(async () => {
    if (!enabled || typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }

    try {
      const { followups } = await fetchPendingFollowups();
      const now = new Date();
      const pendingIds = new Set(followups.map((followup: any) => String(followup.id)));
      const activeNotifications = inAppNotifications.current.filter((notification) => (
        notification.type !== 'followup' || !notification.followupId || pendingIds.has(notification.followupId)
      ));
      if (activeNotifications.length !== inAppNotifications.current.length) {
        persistNotifications(activeNotifications);
      }

      followups.forEach((fu: any) => {
        const followupDateTime = new Date(fu.followup_date + 'T00:00:00');

        if (fu.followup_time) {
          const [hours, minutes] = fu.followup_time.split(':');
          followupDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        } else {
          followupDateTime.setHours(23, 59, 59, 999);
        }

        if (followupDateTime <= now && !notifiedFollowups.current.has(fu.id)) {
          const leadName = fu.name || 'Unknown';
          const leadPhone = fu.phone || '';
          const notification: InAppNotification = {
            id: fu.id,
            type: 'followup',
            title: 'Follow-up Reminder',
            message: `${leadName} (${leadPhone}) - Follow-up due ${fu.followup_date}${fu.followup_time ? ` at ${fu.followup_time}` : ''}${fu.notes ? `\nNotes: ${fu.notes}` : ''}`,
            timestamp: now,
            read: false,
            followupId: fu.id,
            followupDate: fu.followup_date,
            followupTime: fu.followup_time || undefined,
            leadId: fu.lead_id,
            leadStatus: fu.lead_status || undefined,
            leadName,
            leadPhone,
          };
          const existingIndex = inAppNotifications.current.findIndex((item) => item.id === notification.id);
          const nextNotifications =
            existingIndex >= 0
              ? inAppNotifications.current.map((item, index) => (index === existingIndex ? { ...item, ...notification, read: item.read } : item))
              : [notification, ...inAppNotifications.current];
          persistNotifications(nextNotifications);
          playNotificationSound();

          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(`Follow-up: ${leadName}`, {
              body: `${leadPhone} - Due: ${fu.followup_date}${fu.followup_time ? ` at ${fu.followup_time}` : ''}${fu.notes ? `\nNotes: ${fu.notes}` : ''}`,
              icon: '/favicon.ico',
              tag: fu.id,
              requireInteraction: true,
            });
          }

          notifiedFollowups.current.add(fu.id);
        }
      });
    } catch (error) {
      console.error('Failed to check follow-ups:', error);
    }
  }, [enabled, persistNotifications]);

  useEffect(() => {
    if (!enabled) return;

    // Request notification permission immediately
    if ('Notification' in window) {
      void Notification.requestPermission();
    }

    const scheduleNext = () => {
      clearTimer();
      if (document.visibilityState !== 'visible') {
        return;
      }
      timerRef.current = setTimeout(async () => {
        await checkFollowups();
        scheduleNext();
      }, 5 * 60 * 1000);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkFollowups().finally(() => {
          scheduleNext();
        });
      } else {
        clearTimer();
      }
    };

    // Check immediately on mount, then pause while the tab is hidden.
    void checkFollowups();
    scheduleNext();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkFollowups, clearTimer, enabled]);

  // Function to get current notifications
  const getNotifications = useCallback(() => {
    return [...inAppNotifications.current];
  }, []);

  // Function to mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    const nextNotifications = inAppNotifications.current.map((notification) =>
      notification.id === notificationId ? { ...notification, read: true } : notification
    );
    persistNotifications(nextNotifications);
  }, [persistNotifications]);

  const markAsReadForLead = useCallback((leadId: string) => {
    const nextNotifications = inAppNotifications.current.map((notification) =>
      notification.leadId === leadId ? { ...notification, read: true } : notification
    );
    persistNotifications(nextNotifications);
  }, [persistNotifications]);

  // Function to clear all notifications
  const clearAll = useCallback(() => {
    persistNotifications([]);
  }, [persistNotifications]);

  // Function to subscribe to notification updates
  const subscribe = useCallback((listener: (notifications: InAppNotification[]) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  // Function to clear notified follow-ups (call when follow-up is completed)
  const clearNotification = useCallback((followupId: string) => {
    notifiedFollowups.current.delete(followupId);
    // Also remove from in-app notifications
    persistNotifications(inAppNotifications.current.filter((n) => n.followupId !== followupId));
  }, [persistNotifications]);

  return { 
    clearNotification, 
    getNotifications, 
    markAsRead, 
    markAsReadForLead,
    clearAll, 
    subscribe 
  };
}
